import React, { useEffect } from "react";
import { Grid, Button, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useAuth } from "hooks";
import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { useLazyGetDDLRouteShipmentsQuery } from "store/services/routeShipmentApi";
import { useLazyListShipmentRoutesQuery } from "store/services/shipmentApi";

const INITIAL_STATE = {
  account: "",
  entity: "",
  shipmentId: "",
  route: "",
  routeStartStop: "",
  routeEndStop: "",
  comments: "",
};

const ShipmentRouteForm = ({
  isBusy = false,
  route = null,
  latestEndStop,
  setLatestEndStop,
  shipmentId,
  onSubmit = () => {},
  onCancel = () => {},
}) => {
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();
  const { ShipmentDetail } = useShipmentDetailContext();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
      route: null,
      routeEndStop: null,
      shipmentId: ShipmentDetail?._id,
      routeStartStop: null,
      comments: "",
    },
  });

  const { data: accounts = [], isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities = [], isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [fetchBranches, { data: branches = [], isLoading: loadingBranches }] =
    useLazyGetDDLBranchListQuery();
  const [fetchRoutes, { data: routes = [], isLoading: loadingRoutes }] =
    useLazyGetDDLRouteShipmentsQuery();
  const [
    fetchShipmentRoutes,
    { data: shipmentRoutesRes, isLoading: loadingShipmentRoutes },
  ] = useLazyListShipmentRoutesQuery();

  const watchAccount = watch("account");
  const watchEntity = watch("entity");
  const watchRoute = watch("route");
  const watchStartStop = watch("routeStartStop");

  // Fetch shipment routes
  useEffect(() => {
    if (shipmentId) {
      fetchShipmentRoutes({ shipmentId, page: 1, limit: 100 });
    }
  }, [shipmentId, fetchShipmentRoutes]);

  // Reset form when editing a route
  useEffect(() => {
    if (!route) return;
    reset({
      ...INITIAL_STATE,
      account: route.account,
      entity: route.entity,
      shipmentId: route.shipmentId || ShipmentDetail?._id,
      route: route.route,
      routeStartStop: route.routeStartStop,
      routeEndStop: route.routeEndStop,
      comments: route.comments || "",
    });
  }, [route, reset, ShipmentDetail?._id]);

  // Set routeStartStop for new routes
  useEffect(() => {
    if (route) return;

    const sortedRoutes =
      shipmentRoutesRes?.data &&
      Array.isArray(shipmentRoutesRes.data) &&
      shipmentRoutesRes.data.length > 0
        ? [...shipmentRoutesRes.data].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          )
        : [];
    const lastRoute = sortedRoutes.length > 0 ? sortedRoutes[0] : null;
    const lastRouteEndStop = lastRoute?.routeEndStop;

    if (
      lastRouteEndStop?._id &&
      branches.some((branch) => branch._id === lastRouteEndStop._id)
    ) {
      setValue("routeStartStop", lastRouteEndStop);
    } else if (
      ShipmentDetail?.sourceBranch &&
      branches.some((branch) => branch._id === ShipmentDetail.sourceBranch._id)
    ) {
      setValue("routeStartStop", ShipmentDetail.sourceBranch);
    } else {
      console.warn("No valid routeStartStop set");
    }
  }, [
    ShipmentDetail?.sourceBranch,
    shipmentRoutesRes,
    setValue,
    route,
    branches,
  ]);

  // Fetch entities
  useEffect(() => {
    if ((isAccountAdmin || isSystemAdmin) && watchAccount?._id) {
      fetchEntities({ account: watchAccount._id });
    }
  }, [watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin]);

  // Fetch branches and routes
  useEffect(() => {
    if (watchEntity?._id) {
      fetchBranches({ account: watchAccount?._id, entity: watchEntity._id });
      fetchRoutes({ account: watchAccount?._id, entity: watchEntity._id });
    }
  }, [watchAccount?._id, watchEntity?._id, fetchBranches, fetchRoutes]);

  const handleOnSubmit = (data) => {
    if (route && !route._id) {
      return;
    }

    const payload = {
      routeId: route?._id,
      account: data.account?._id ?? viewEntity?.entity?.account?._id,
      entity: data.entity?._id ?? viewEntity?.entity?._id,
      shipmentId: ShipmentDetail?._id,
      route: data.route?._id,
      routeStartStop: data.routeStartStop?._id,
      routeEndStop: data.routeEndStop?._id,
      comments: data.comments,
    };

    if (data.routeEndStop?._id) {
      setLatestEndStop(data.routeEndStop);
    } else {
      console.warn("Invalid routeEndStop:", data.routeEndStop);
    }

    onSubmit(payload);
  };

  const getStopDetails = (branch) => {
    if (!watchRoute?.routeStops || !watchStartStop) {
      return `${branch.name ?? ""} - ${branch.code ?? ""}`;
    }
    const relevantStop = watchRoute.routeStops.find(
      (stop) =>
        stop.stopOrigin?._id === watchStartStop?._id &&
        stop.stopDestination?._id === branch._id
    );
    if (!relevantStop) {
      return `${branch.name ?? ""} - ${branch.code ?? ""}`;
    }
    return `${branch.name ?? ""} - ${branch.code ?? ""}`;
  };

  // Get valid end stops based on selected route's routeStops, excluding the first stopOrigin
  const getValidEndStops = () => {
    if (!watchRoute?.routeStops || !watchStartStop) {
      return [];
    }
    // Sort routeStops by serialNumber to ensure correct order
    const sortedStops = [...watchRoute.routeStops].sort(
      (a, b) => (a.serialNumber || 0) - (b.serialNumber || 0)
    );
    // Get the first stopOrigin
    const firstStopOrigin = sortedStops[0]?.stopOrigin?._id;

    // Collect all stopOrigin and stopDestination, excluding the first stopOrigin
    const validStops = new Set();
    sortedStops.forEach((stop) => {
      if (
        stop.stopOrigin?._id &&
        stop.stopOrigin._id !== firstStopOrigin &&
        stop.stopOrigin._id !== watchStartStop?._id
      ) {
        validStops.add(JSON.stringify(stop.stopOrigin));
      }
      if (
        stop.stopDestination?._id &&
        stop.stopDestination._id !== watchStartStop?._id
      ) {
        validStops.add(JSON.stringify(stop.stopDestination));
      }
    });

    return Array.from(validStops)
      .map((stop) => JSON.parse(stop))
      .filter((stop) => stop && stop._id);
  };

  return (
    <AppForm loading={isBusy || loadingShipmentRoutes}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={12}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", null);
                      setValue("route", null);
                      setValue("routeEndStop", null);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts}
                    loading={loadingAccounts}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                    disabled
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={12}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Entity"
                    required
                    value={field.value ?? null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("route", null);
                      setValue("routeEndStop", null);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities}
                    loading={loadingEntities}
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                    disabled
                  />
                )}
              />
            </Grid>
          )}
          <Grid item md={12}>
            <Controller
              name="route"
              control={control}
              rules={{ required: "Please select a route." }}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  label="Route"
                  required
                  value={field.value ?? null}
                  onChange={(_, data) => {
                    field.onChange(data);
                    setValue("routeEndStop", null);
                  }}
                  getOptionLabel={(option) => option?.routeName ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={routes}
                  loading={loadingRoutes}
                  helperText={errors?.route?.message}
                  error={!!errors?.route}
                />
              )}
            />
          </Grid>
          <Grid item md={12}>
            <Controller
              name="routeStartStop"
              control={control}
              rules={{ required: "Please select a start stop." }}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  label="Start Stop"
                  required
                  disabled
                  value={field.value ?? null}
                  onChange={(_, data) => {
                    field.onChange(data);
                    setValue("routeEndStop", null);
                  }}
                  getOptionLabel={(option) =>
                    `${option?.name ?? ""} - ${option?.code ?? ""}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={branches}
                  loading={loadingBranches}
                  helperText={errors?.routeStartStop?.message}
                  error={!!errors?.routeStartStop}
                />
              )}
            />
          </Grid>
          <Grid item md={12}>
            <Controller
              name="routeEndStop"
              control={control}
              // rules={{ required: "Please select an end stop." }}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  label="End Stop"
                  // required
                  value={field.value ?? null}
                  onChange={(_, data) => field.onChange(data)}
                  getOptionLabel={getStopDetails}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={getValidEndStops()}
                  loading={loadingBranches}
                  helperText={errors?.routeEndStop?.message}
                  error={!!errors?.routeEndStop}
                />
              )}
            />
          </Grid>
          <Grid item md={12}>
            <Controller
              name="comments"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Comments"
                  multiline
                  rows={4}
                  helperText={errors?.comments?.message}
                  error={!!errors?.comments}
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button disabled={isBusy || loadingShipmentRoutes} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isBusy || loadingShipmentRoutes}
          onClick={handleSubmit(handleOnSubmit)}
        >
          {route ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default ShipmentRouteForm;
